import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Grid,
  TextField,
  IconButton,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Alert,
  Chip,
  Checkbox
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalanceWallet as WalletIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  MoreVert as MoreVertIcon,
  Payment as PaymentIcon,
  Book as LedgerIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  FileDownload as FileDownloadIcon,
  Chat as ChatIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  Groups as GroupsIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import ReportMetricCard from '../../components/reports/ReportMetricCard';
import { reportPageSx } from '../../components/reports/reportPageLayout';
import { customerService } from '../../services/customerService';
import { enqueueSnackbar } from 'notistack';
import axios from 'axios';
import { getAuthHeader } from '../../services/authService';
import API_CONFIG from '../../config';
import creditService from '../../services/creditService';
import dashboardService from '../../services/dashboardService';
import companySettingsService from '../../services/companySettingsService';
import * as XLSX from 'xlsx';
import { formatCurrency as formatCurrencyGlobal, getCurrencySymbol } from '../../utils/currencyFormatter';
import whatsappSettingsService from '../../services/whatsappSettingsService';
import { getCurrentUser } from '../../services/authService';
import {
  isDesktopApp,
  formatPhoneNumberForWhatsApp,
  getWhatsAppStatus,
  sendPaymentReminderForCustomerDesktop,
  sendPaymentRemindersSequentialDesktop,
} from '../../utils/whatsappShareHelper';
import {
  getRemainingReminderSends,
  incrementSuccessfulReminderSends,
  PAYMENT_REMINDER_DAILY_LIMIT,
} from '../../utils/paymentReminderDailyQuota';

// Helper function to format a date string or return a default value if invalid
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    // Try to parse the date
    const date = new Date(dateString);

    // Check if the date is valid
    if (date instanceof Date && !isNaN(date)) {
      // Format as local date string
      return date.toLocaleDateString();
    }

    return 'N/A';
  } catch (error) {
    console.error('Error formatting date:', error, dateString);
    return 'N/A';
  }
};

// Helper to get created date from customer object, checking all possible field names
const getCustomerCreatedDate = (customer) => {
  // Log the entire customer object to debug
  console.log('Customer object:', customer);

  // Try all possible field names
  return customer.createdAt || customer.created_at;
};

// Helper function to format currency values safely
const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return formatCurrencyGlobal(0);
  }
  return formatCurrencyGlobal(value);
};

const CreditCustomersReport = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalCreditAmount, setTotalCreditAmount] = useState(0);
  const [activeCreditCustomersCount, setActiveCreditCustomersCount] = useState(0);

  // For dropdown action menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);

  // For payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [paymentNote, setPaymentNote] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [creditSalesData, setCreditSalesData] = useState({
    totalSale: 0,
    totalPaid: 0,
    openingBalance: 0,
    currentBalance: 0,
    transactions: []
  });
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [error, setError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // New state variables for "View All Transactions" feature
  const [allTransactionsDialogOpen, setAllTransactionsDialogOpen] = useState(false);
  const [allCustomersWithHistory, setAllCustomersWithHistory] = useState([]);
  const [selectedHistoryCustomer, setSelectedHistoryCustomer] = useState(null);
  const [loadingAllCustomers, setLoadingAllCustomers] = useState(false);
  const [showingHistoryLedger, setShowingHistoryLedger] = useState(false);
  const [historySearchTerm, setHistorySearchTerm] = useState(''); // Search for all transactions dialog

  // Desktop WhatsApp payment reminders (manual only; daily cap in paymentReminderDailyQuota)
  const [usagePaymentReminder, setUsagePaymentReminder] = useState(false);
  const [waSessionReady, setWaSessionReady] = useState(false);
  const [reminderQuotaRemaining, setReminderQuotaRemaining] = useState(PAYMENT_REMINDER_DAILY_LIMIT);
  const [selectedReminderIds, setSelectedReminderIds] = useState([]);
  const [bulkReminderConfirmOpen, setBulkReminderConfirmOpen] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  const [reminderCompanyName, setReminderCompanyName] = useState(() =>
    (getCurrentUser().companyName || '').trim()
  );
  const [reminderCompanyContact, setReminderCompanyContact] = useState('');

  const navigate = useNavigate();

  const desktopReminderUiEnabled = isDesktopApp() && usagePaymentReminder;

  const getReminderCompanyMeta = () => {
    const u = getCurrentUser();
    const name = (reminderCompanyName || u.companyName || '').trim() || 'Our business';
    const contact = (reminderCompanyContact || '').trim();
    return { companyName: name, companyContact: contact };
  };

  const syncReminderQuotaFromStorage = () => {
    if (!isDesktopApp()) return;
    const { companyId } = getCurrentUser();
    setReminderQuotaRemaining(getRemainingReminderSends(companyId));
  };

  // Fetch credit customers from the API using centralized service
  const fetchCustomers = async () => {
    try {
      setLoading(true);

      // Use the centralized dashboard service for consistency
      const creditData = await dashboardService.getCreditCustomersData();

      setCustomers(creditData.customers);
      setTotalCreditAmount(creditData.totalDueAmount);
      setActiveCreditCustomersCount(creditData.totalCreditCustomers);

      console.log('📊 Credit Customers Report Results (using centralized service):');
      console.log('   Customers with positive balance due:', creditData.totalCreditCustomers);
      console.log('   Total balance due:', creditData.totalDueAmount);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching customers:', error);
      enqueueSnackbar('Failed to load customer data', { variant: 'error' });
      setLoading(false);
    } finally {
      if (isDesktopApp()) {
        syncReminderQuotaFromStorage();
      }
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (!isDesktopApp()) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const data = await whatsappSettingsService.get();
        if (!cancelled) setUsagePaymentReminder(Boolean(data.usagePaymentReminder));
      } catch {
        if (!cancelled) setUsagePaymentReminder(false);
      }
      try {
        const st = await getWhatsAppStatus();
        if (!cancelled) setWaSessionReady(Boolean(st.ready));
      } catch {
        if (!cancelled) setWaSessionReady(false);
      }
      if (!cancelled) {
        const { companyId } = getCurrentUser();
        setReminderQuotaRemaining(getRemainingReminderSends(companyId));
      }
      try {
        const co = await companySettingsService.getSettings();
        if (!cancelled && co) {
          const nameFromSettings = (co.name && String(co.name).trim()) || '';
          const contactRaw = co.contact != null ? String(co.contact).trim() : '';
          const u = getCurrentUser();
          setReminderCompanyName(nameFromSettings || (u.companyName || '').trim() || 'Our business');
          setReminderCompanyContact(contactRaw);
        }
      } catch {
        if (!cancelled) {
          const u = getCurrentUser();
          setReminderCompanyName((u.companyName || '').trim() || 'Our business');
          setReminderCompanyContact('');
        }
      }
    };
    tick();
    const iv = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, []);

  useEffect(() => {
    if (!desktopReminderUiEnabled) {
      setSelectedReminderIds([]);
    }
  }, [desktopReminderUiEnabled]);

  useEffect(() => {
    setSelectedReminderIds((prev) => {
      if (prev.length <= reminderQuotaRemaining) return prev;
      return prev.slice(0, reminderQuotaRemaining);
    });
  }, [reminderQuotaRemaining]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRefresh = () => {
    fetchCustomers();
  };

  // Action menu handlers
  const handleOpenMenu = (event, customerId) => {
    setAnchorEl(event.currentTarget);
    setSelectedCustomerId(customerId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedCustomerId(null);
  };

  const handlePayAction = () => {
    // Find the selected customer
    const customer = customers.find(c => c.id === selectedCustomerId);
    if (customer) {
      setSelectedCustomer(customer);
      setError(null); // Clear any previous errors

      // Set credit sales data with initial values from customer's balance due
      setCreditSalesData({
        totalSale: 0,
        totalPaid: 0,
        openingBalance: 0,
        currentBalance: parseFloat(customer.balanceDue || 0),
        transactions: []
      });

      // Set default payment fields
      setPaymentAmount(customer.balanceDue ? customer.balanceDue.toString() : '0');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentMethod('Cash');
      setPaymentNote('');

      // Open dialog immediately with initial data
      setPaymentDialogOpen(true);

      // Fetch additional data asynchronously (pass customer so fallback works even before state settles)
      fetchCustomerCreditData(customer.id, customer);
    }
    handleCloseMenu();
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setSelectedCustomer(null);
    setPaymentAmount('');
  };

  const handlePaymentAmountChange = (event) => {
    const value = event.target.value;
    // Only allow numeric input with up to 2 decimal places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setPaymentAmount(value);
    }
  };

  const handleSubmitPayment = async () => {
    if (!selectedCustomer) return;

    try {
      setProcessingPayment(true);
      setError(null);

      const paymentData = {
        amount: parseFloat(paymentAmount),
        paymentMethod: paymentMethod,
        notes: paymentNote,
        referenceId: 'MANUAL-PAYMENT-' + new Date().getTime(), // Add reference ID for manual payments
        transactionDate: new Date(paymentDate).toISOString()
      };

      await creditService.processPayment(selectedCustomer.id, paymentData);

      // Show success message
      enqueueSnackbar('Payment processed successfully', { variant: 'success' });

      // Refresh data
      handleClosePaymentDialog();
      fetchCustomers();
    } catch (err) {
      console.error('Error processing payment:', err);
      setError(err.message || 'Failed to process payment. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleLedgerAction = () => {
    console.log('Ledger action for customer ID:', selectedCustomerId);
    // Navigate to the customer ledger page with the customer ID
    navigate(`/customer-credit-ledger/${selectedCustomerId}`);
    handleCloseMenu();
  };

  const isCustomerEligibleForWaReminder = (customer) => {
    const due = parseFloat(customer.balanceDue || 0);
    if (due <= 0) return false;
    const waPhone = formatPhoneNumberForWhatsApp(
      customer.phone != null ? String(customer.phone) : ''
    );
    return Boolean(waPhone);
  };

  const handleReminderRowToggle = (customerId) => {
    if (!desktopReminderUiEnabled || sendingReminders) return;
    setSelectedReminderIds((prev) => {
      if (prev.includes(customerId)) {
        return prev.filter((id) => id !== customerId);
      }
      if (prev.length >= reminderQuotaRemaining) {
        enqueueSnackbar(
          `You can select at most ${reminderQuotaRemaining} customer(s) today (daily WhatsApp limit: ${PAYMENT_REMINDER_DAILY_LIMIT}).`,
          { variant: 'warning' }
        );
        return prev;
      }
      return [...prev, customerId];
    });
  };

  const handleReminderSelectAllOnPage = (pageRows) => {
    if (!desktopReminderUiEnabled || sendingReminders) return;
    const eligibleOnPage = pageRows.filter(isCustomerEligibleForWaReminder);
    const eligibleIds = eligibleOnPage.map((c) => c.id);
    if (eligibleIds.length === 0) return;

    setSelectedReminderIds((prev) => {
      const allOnPageSelected =
        eligibleIds.length > 0 && eligibleIds.every((id) => prev.includes(id));
      if (allOnPageSelected) {
        return prev.filter((id) => !eligibleIds.includes(id));
      }
      const next = new Set(prev);
      for (const id of eligibleIds) {
        if (next.size >= reminderQuotaRemaining) break;
        next.add(id);
      }
      const result = Array.from(next);
      const addedAllEligible = eligibleIds.every((id) => result.includes(id));
      if (!addedAllEligible) {
        setTimeout(() => {
          enqueueSnackbar(
            `Only ${reminderQuotaRemaining} customer(s) can be selected today (daily limit: ${PAYMENT_REMINDER_DAILY_LIMIT}).`,
            { variant: 'warning' }
          );
        }, 0);
      }
      return result;
    });
  };

  const handleOpenBulkReminderConfirm = () => {
    if (!desktopReminderUiEnabled) return;
    if (!waSessionReady) {
      enqueueSnackbar(
        'WhatsApp is not connected. Open Settings → WhatsApp Configuration and scan the QR code.',
        { variant: 'warning' }
      );
      return;
    }
    if (reminderQuotaRemaining < 1) {
      enqueueSnackbar('Daily WhatsApp payment reminder limit reached for today.', {
        variant: 'warning',
      });
      return;
    }
    const chosen = customers.filter(
      (c) => selectedReminderIds.includes(c.id) && isCustomerEligibleForWaReminder(c)
    );
    if (chosen.length === 0) {
      enqueueSnackbar(
        'Select at least one customer with balance due and a valid phone number.',
        { variant: 'warning' }
      );
      return;
    }
    setBulkReminderConfirmOpen(true);
  };

  const handleCloseBulkReminderConfirm = () => {
    setBulkReminderConfirmOpen(false);
  };

  const handleConfirmBulkReminderSend = async () => {
    const chosen = customers.filter(
      (c) => selectedReminderIds.includes(c.id) && isCustomerEligibleForWaReminder(c)
    );
    if (chosen.length > reminderQuotaRemaining) {
      enqueueSnackbar('Selection exceeds remaining sends for today.', { variant: 'error' });
      return;
    }
    setBulkReminderConfirmOpen(false);
    setSendingReminders(true);
    const { companyId } = getCurrentUser();
    const items = chosen.map((customer) => ({
      customer,
      balanceFormatted: formatCurrency(customer.balanceDue),
    }));
    let successCount = 0;
    try {
      const rows = await sendPaymentRemindersSequentialDesktop(items, {
        delayMs: 1500,
        companyMeta: getReminderCompanyMeta(),
      });
      rows.forEach(({ result }) => {
        if (result.success) {
          successCount += 1;
          incrementSuccessfulReminderSends(companyId, 1);
        }
      });
      setReminderQuotaRemaining(getRemainingReminderSends(companyId));
      const fail = rows.length - successCount;
      enqueueSnackbar(
        `Payment reminders: ${successCount} sent${fail ? `, ${fail} failed` : ''}.`,
        { variant: successCount ? 'success' : 'warning' }
      );
      setSelectedReminderIds([]);
      fetchCustomers();
    } catch (e) {
      console.error(e);
      enqueueSnackbar('Bulk reminder send failed.', { variant: 'error' });
    } finally {
      setSendingReminders(false);
    }
  };

  const handleSendWhatsAppReminderAction = async () => {
    const customerId = selectedCustomerId;
    handleCloseMenu();
    if (!customerId) return;
    const customer = customers.find((c) => c.id === customerId);
    if (!customer) return;
    if (!desktopReminderUiEnabled) {
      enqueueSnackbar('Enable Payment reminder in Settings → WhatsApp Configuration.', {
        variant: 'info',
      });
      return;
    }
    if (!waSessionReady) {
      enqueueSnackbar('WhatsApp is not connected.', { variant: 'warning' });
      return;
    }
    if (reminderQuotaRemaining < 1) {
      enqueueSnackbar('Daily WhatsApp payment reminder limit reached for today.', {
        variant: 'warning',
      });
      return;
    }
    if (!isCustomerEligibleForWaReminder(customer)) {
      enqueueSnackbar('This customer needs balance due and a valid phone number.', {
        variant: 'warning',
      });
      return;
    }
    setSendingReminders(true);
    const { companyId } = getCurrentUser();
    try {
      const res = await sendPaymentReminderForCustomerDesktop(
        customer,
        formatCurrency(customer.balanceDue),
        getReminderCompanyMeta()
      );
      if (res.success) {
        incrementSuccessfulReminderSends(companyId, 1);
        setReminderQuotaRemaining(getRemainingReminderSends(companyId));
        setSelectedReminderIds((prev) => prev.filter((id) => id !== customerId));
        enqueueSnackbar('Payment reminder sent.', { variant: 'success' });
      } else {
        enqueueSnackbar(res.error || 'Failed to send reminder.', { variant: 'error' });
      }
    } finally {
      setSendingReminders(false);
    }
  };

  const fetchCustomerCreditData = async (customerId, customerObj) => {
    try {
      setError(null);

      const [summary, transactions] = await Promise.all([
        creditService.getCustomerCreditSummary(customerId),
        creditService.getCustomerTransactions(customerId)
      ]);

      if (summary) {
        // Apply fallback: if backend summary balanceDue=0 but customer creditBalance>0
        // (happens for split-bill partial credit sales), use creditBalance as the true balance
        const summaryBalanceDue = parseFloat(summary.balanceDue || 0);
        const customerCreditBal = parseFloat(
          (customerObj || selectedCustomer)?.creditBalance ||
          (customerObj || selectedCustomer)?.credit_balance ||
          0
        );
        // customers.credit_balance is the authoritative source — always updated correctly by backend.
        // The transaction running_balance can go negative after payments (backend bug).
        // Use creditBalance when positive; fall back to max(0, summaryBalanceDue) otherwise.
        const effectiveBalanceDue = customerCreditBal > 0 ? customerCreditBal : Math.max(0, summaryBalanceDue);

        const updatedCreditData = {
          totalSale: summary.totalCreditSales || 0,        // Total from credit sales orders
          totalPaid: summary.totalPayments || 0,           // Sum of all payments
          openingBalance: summary.openingBalance || 0,     // Opening balance from customers table
          currentBalance: effectiveBalanceDue,             // Use effective balance (with fallback)
          transactions: transactions || []
        };

        setCreditSalesData(updatedCreditData);

        // Update the payment amount to reflect the correct balance due
        setPaymentAmount(effectiveBalanceDue.toString());

        console.log('📊 Fresh credit data loaded:', {
          customerId,
          balanceDueFromAPI: summaryBalanceDue,
          customerCreditBalance: customerCreditBal,
          effectiveBalanceDue,
          openingBalance: summary.openingBalance,
          totalCreditSales: summary.totalCreditSales,
          totalPayments: summary.totalPayments
        });
      }
    } catch (err) {
      console.error('Error fetching credit data:', err);
      setError('Failed to load customer credit data. Please try again.');
    }
  };

  // New handlers for "View All Transactions" feature
  const handleViewAllTransactions = async () => {
    try {
      setLoadingAllCustomers(true);
      setAllTransactionsDialogOpen(true);

      const allCustomers = await creditService.getAllCustomersWithCreditHistory();
      setAllCustomersWithHistory(allCustomers);

      console.log('📊 All Customers with Credit History:', allCustomers.length);
    } catch (error) {
      console.error('Error fetching customers with credit history:', error);
      enqueueSnackbar('Failed to load customers with credit history', { variant: 'error' });
    } finally {
      setLoadingAllCustomers(false);
    }
  };

  const handleCloseAllTransactionsDialog = () => {
    setAllTransactionsDialogOpen(false);
    setSelectedHistoryCustomer(null);
    setShowingHistoryLedger(false);
    setAllCustomersWithHistory([]);
    setHistorySearchTerm(''); // Reset search when closing
  };

  const handleSelectHistoryCustomer = (customer) => {
    setSelectedHistoryCustomer(customer);
    setShowingHistoryLedger(true);
  };

  const handleBackToCustomerList = () => {
    setSelectedHistoryCustomer(null);
    setShowingHistoryLedger(false);
  };

  const handleHistorySearchChange = (event) => {
    setHistorySearchTerm(event.target.value);
  };

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      // Prepare export data from customers
      const exportData = customers.map((cust, idx) => ({
        'S.No': idx + 1,
        'Customer Name': cust.name,
        'Phone': cust.phone,
        'Email': cust.email,
        'Balance Due': cust.balanceDue,
        'Total Returns': cust.totalReturns,
        'Created At': formatDate(cust.createdAt)
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        { wch: 5 }, { wch: 28 }, { wch: 15 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 18 }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.sheet_add_aoa(ws, [
        ['Credit Customers Report'],
        ['Generated on: ' + new Date().toLocaleString()],
        ['']
      ], { origin: 'A1' });
      XLSX.utils.book_append_sheet(wb, ws, 'Credit Customers');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const fileData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(fileData);
      const link = document.createElement('a');
      link.href = url;
      link.download = `credit_customers_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar && enqueueSnackbar('Credit customers report exported successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar && enqueueSnackbar('Failed to export credit customers report', { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDebugCreditCustomers = async () => {
    try {
      setLoading(true);
      console.log('🔍 Starting debug analysis...');

      // Get all customers with credit history
      const allCustomers = await creditService.getAllCustomersWithCreditHistory();
      console.log('📊 All customers with credit history:', allCustomers.length);

      // Use the centralized dashboard service for comparison
      const creditData = await dashboardService.getCreditCustomersData();
      console.log('📊 Customers showing in main report:', creditData.totalCreditCustomers);

      // Compare the two datasets
      console.log('🔍 DETAILED COMPARISON:');
      console.log('----------------------------------------');

      allCustomers.forEach(customer => {
        const isInMainReport = creditData.customers.find(c => c.id === customer.id);
        console.log(`Customer: ${customer.name} (ID: ${customer.id})`);
        console.log(`  - Credit Balance: ${customer.creditBalance}`);
        console.log(`  - Opening Balance: ${customer.openingBalance}`);
        console.log(`  - Total Credit Sales: ${customer.totalCreditSales}`);
        console.log(`  - Total Payments: ${customer.totalPayments}`);
        console.log(`  - Total Returns: ${customer.totalReturns}`);
        console.log(`  - Balance Due: ${customer.balanceDue}`);
        console.log(`  - In Main Report: ${isInMainReport ? 'YES' : 'NO'}`);
        console.log(`  - Reason for exclusion: ${customer.balanceDue <= 0 ? 'Balance Due is zero or negative' : 'Should be included'}`);
        console.log('----------------------------------------');
      });

      // Summary
      const customersWithPositiveBalance = allCustomers.filter(c => c.balanceDue > 0);
      const customersWithZeroBalance = allCustomers.filter(c => c.balanceDue <= 0);

      console.log('🎯 SUMMARY:');
      console.log(`Total customers with transaction history: ${allCustomers.length}`);
      console.log(`Customers with positive balance due: ${customersWithPositiveBalance.length}`);
      console.log(`Customers with zero/negative balance due: ${customersWithZeroBalance.length}`);

      // Show alert with summary
      alert(`Debug Results:
Total customers with credit history: ${allCustomers.length}
Customers with positive balance (showing in report): ${customersWithPositiveBalance.length}
Customers with zero/negative balance (filtered out): ${customersWithZeroBalance.length}

Check console for detailed breakdown.`);

    } catch (error) {
      console.error('Error in debug analysis:', error);
      alert('Error running debug analysis. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(customer.phone || '').includes(searchTerm) ||
    (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const paginatedCustomers = filteredCustomers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const eligibleReminderOnPage = desktopReminderUiEnabled
    ? paginatedCustomers.filter(isCustomerEligibleForWaReminder)
    : [];
  const eligibleReminderIdsOnPage = eligibleReminderOnPage.map((c) => c.id);
  const selectedEligibleOnPageCount = eligibleReminderIdsOnPage.filter((id) =>
    selectedReminderIds.includes(id)
  ).length;
  const allReminderPageSelected =
    eligibleReminderIdsOnPage.length > 0 &&
    selectedEligibleOnPageCount === eligibleReminderIdsOnPage.length;
  const someReminderPageSelected =
    selectedEligibleOnPageCount > 0 && !allReminderPageSelected;

  // Filter customers with history based on search term
  const filteredHistoryCustomers = allCustomersWithHistory.filter(customer =>
    customer.name.toLowerCase().includes(historySearchTerm.toLowerCase())
  );

  const positiveBalanceCount = customers.filter((c) => parseFloat(c.balanceDue || 0) > 0).length;
  const zeroOrPaidCount = customers.filter((c) => parseFloat(c.balanceDue || 0) <= 0).length;

  return (
    <Layout>
      <Container maxWidth="xl" sx={reportPageSx.container}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <Typography variant="h4" sx={{ ...reportPageSx.pageTitle, m: 0 }}>
            Customer Payment
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={handleExportToExcel}
            disabled={isExporting || customers.length === 0}
            startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
          >
            {isExporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </Box>

        <Grid container spacing={reportPageSx.metricsGrid.spacing} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <ReportMetricCard
              label="Total Balance Due"
              value={loading ? '…' : formatCurrency(totalCreditAmount)}
              icon={<CurrencyRupeeIcon />}
              accentColor="error.main"
              valueColor="error.main"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <ReportMetricCard
              label="Credit Customers"
              value={loading ? '…' : activeCreditCustomersCount}
              icon={<PersonIcon />}
              accentColor="primary.main"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <ReportMetricCard
              label="Owing Balance"
              value={loading ? '…' : positiveBalanceCount}
              icon={<GroupsIcon />}
              accentColor="warning.main"
              valueColor="warning.main"
              caption="Customers with amount due"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <ReportMetricCard
              label="Zero / Paid"
              value={loading ? '…' : zeroOrPaidCount}
              icon={<CheckCircleIcon />}
              accentColor="success.main"
              valueColor="success.main"
            />
          </Grid>
        </Grid>

        <Card sx={reportPageSx.filterCard}>
          <CardContent sx={reportPageSx.filterCardContent}>
            {desktopReminderUiEnabled && (
              <Box sx={{ mb: 1.5 }}>
                {reminderQuotaRemaining < 1 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Daily WhatsApp payment reminder limit reached for today. You can send again tomorrow.
                  </Alert>
                )}
                {reminderQuotaRemaining > 0 && !waSessionReady && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    WhatsApp is not connected. Open Settings → WhatsApp Configuration and scan the QR code to send
                    reminders.
                  </Alert>
                )}
                <Chip
                  size="small"
                  color={reminderQuotaRemaining < 1 ? 'default' : 'primary'}
                  label={`WhatsApp reminders left today: ${reminderQuotaRemaining} / ${PAYMENT_REMINDER_DAILY_LIMIT}`}
                />
              </Box>
            )}
            <Grid
              container
              spacing={reportPageSx.filterGrid.spacing}
              alignItems="flex-end"
              sx={{ flexWrap: { xs: 'wrap', lg: 'nowrap' } }}
            >
              <Grid item xs={12} lg={4} sx={{ minWidth: 0 }}>
                <TextField
                  fullWidth
                  label="Search customers"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Name, phone, or email"
                  size="small"
                />
              </Grid>
              {desktopReminderUiEnabled && (
                <Grid item xs={12} sm={6} lg={3} sx={{ minWidth: 0, flexShrink: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={sendingReminders ? <CircularProgress size={18} color="inherit" /> : <ChatIcon />}
                    onClick={handleOpenBulkReminderConfirm}
                    disabled={
                      sendingReminders ||
                      selectedReminderIds.length === 0 ||
                      !waSessionReady ||
                      reminderQuotaRemaining < 1
                    }
                    sx={reportPageSx.filterButton}
                  >
                    Bulk reminders
                  </Button>
                </Grid>
              )}
              <Grid item xs={12} sm={6} lg={2} sx={{ minWidth: 0, flexShrink: 0 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={reportPageSx.filterButton}
                >
                  Refresh
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} lg={3} sx={{ minWidth: 0, flexShrink: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  onClick={() => navigate('/people/customers')}
                  sx={reportPageSx.filterButton}
                >
                  Add credit customer
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={reportPageSx.sectionCard}>
          <CardContent sx={{ ...reportPageSx.sectionCardContent, p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {desktopReminderUiEnabled && (
                        <TableCell padding="checkbox" sx={{ width: 48 }}>
                          <Checkbox
                            indeterminate={someReminderPageSelected}
                            checked={allReminderPageSelected}
                            onChange={() => handleReminderSelectAllOnPage(paginatedCustomers)}
                            disabled={
                              sendingReminders ||
                              reminderQuotaRemaining < 1 ||
                              !waSessionReady ||
                              eligibleReminderIdsOnPage.length === 0
                            }
                            inputProps={{ 'aria-label': 'Select customers on this page for payment reminders' }}
                          />
                        </TableCell>
                      )}
                      <TableCell>S.No</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Balance Due</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCustomers.map((customer, index) => (
                        <TableRow key={customer.id}>
                          {desktopReminderUiEnabled && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedReminderIds.includes(customer.id)}
                                onChange={() => handleReminderRowToggle(customer.id)}
                                disabled={
                                  sendingReminders ||
                                  !isCustomerEligibleForWaReminder(customer) ||
                                  (!selectedReminderIds.includes(customer.id) &&
                                    (reminderQuotaRemaining < 1 || !waSessionReady))
                                }
                                inputProps={{
                                  'aria-label': `Select ${customer.name || 'customer'} for payment reminder`,
                                }}
                              />
                            </TableCell>
                          )}
                          <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell>{customer.id}</TableCell>
                          <TableCell>{customer.name}</TableCell>
                          <TableCell>{customer.phone}</TableCell>
                          <TableCell>{customer.email}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                              {parseFloat(customer.balanceDue || 0) > 0 ? (
                                <>
                                  <WalletIcon color="error" fontSize="small" />
                                  <Typography color="error.main" fontWeight="bold">
                                    {formatCurrency(customer.balanceDue)}
                                  </Typography>
                                </>
                              ) : parseFloat(customer.balanceDue || 0) === 0 ? (
                                <>
                                  <WalletIcon color="success" fontSize="small" />
                                  <Typography color="success.main">
                                    {formatCurrency(customer.balanceDue)}
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <WalletIcon color="primary" fontSize="small" />
                                  <Typography color="primary.main">
                                    {formatCurrency(customer.balanceDue)} (Credit)
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              endIcon={<MoreVertIcon fontSize="small" />}
                              onClick={(event) => handleOpenMenu(event, customer.id)}
                            >
                              Actions
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                size="small"
                count={filteredCustomers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />

              {/* Actions Menu */}
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={handlePayAction}>
                  <ListItemIcon>
                    <PaymentIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Pay" />
                </MenuItem>
                <MenuItem onClick={handleLedgerAction}>
                  <ListItemIcon>
                    <LedgerIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Ledger" />
                </MenuItem>
                {desktopReminderUiEnabled && (
                  <MenuItem
                    onClick={handleSendWhatsAppReminderAction}
                    disabled={sendingReminders || !waSessionReady || reminderQuotaRemaining < 1}
                  >
                    <ListItemIcon>
                      <ChatIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Send WhatsApp payment reminder" />
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
          </CardContent>
        </Card>
      </Container>

      <Dialog
        open={bulkReminderConfirmOpen}
        onClose={handleCloseBulkReminderConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send payment reminders?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Send WhatsApp payment reminders to{' '}
            <strong>
              {
                customers.filter(
                  (c) => selectedReminderIds.includes(c.id) && isCustomerEligibleForWaReminder(c)
                ).length
              }
            </strong>{' '}
            selected customer(s) using the WhatsApp session linked on this computer.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Successful sends count toward the daily limit of {PAYMENT_REMINDER_DAILY_LIMIT} on this device ({' '}
            {reminderQuotaRemaining} remaining before this batch).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkReminderConfirm} disabled={sendingReminders}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmBulkReminderSend}
            disabled={sendingReminders}
            startIcon={sendingReminders ? <CircularProgress size={18} color="inherit" /> : <ChatIcon />}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" component="div">Process Payment</Typography>
          <IconButton
            onClick={handleClosePaymentDialog}
            size="small"
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {selectedCustomer && (
            <Grid container spacing={3}>
              {/* Customer Info Section */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>
                    Customer Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                        <strong>Name:</strong> {selectedCustomer.name}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Phone:</strong> {selectedCustomer.phone}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Total Sale:</strong> {formatCurrency(creditSalesData.totalSale)}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Total Paid:</strong> {formatCurrency(creditSalesData.totalPaid)}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Current Balance:</strong> {formatCurrency(creditSalesData.currentBalance)}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Opening Balance:</strong> {formatCurrency(selectedCustomer.openingBalance || 0)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Payment Details */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Payment Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      id="payment-amount-input"
                      name="payment-amount"
                      fullWidth
                      required
                      label="Payment Amount"
                      value={paymentAmount}
                      onChange={handlePaymentAmountChange}
                      placeholder="Enter payment amount"
                      type="number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {getCurrencySymbol()}
                          </InputAdornment>
                        ),
                        inputProps: {
                          'aria-label': 'Payment amount',
                          min: 0,
                          max: creditSalesData.currentBalance,
                          step: "0.01"
                        }
                      }}
                      error={parseFloat(paymentAmount) > creditSalesData.currentBalance}
                      helperText={
                        parseFloat(paymentAmount) > creditSalesData.currentBalance
                          ? "Amount cannot exceed current balance"
                          : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="payment-date-input"
                      name="payment-date"
                      fullWidth
                      required
                      label="Payment Date"
                      type="date"
                      value={paymentDate}
                      onChange={(e) => setPaymentDate(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        'aria-label': 'Payment date'
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="payment-method-input"
                      name="payment-method"
                      fullWidth
                      required
                      select
                      label="Payment Method"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      inputProps={{
                        'aria-label': 'Payment method'
                      }}
                    >
                      <MenuItem value="Cash">Cash</MenuItem>
                      <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                      <MenuItem value="Cheque">Cheque</MenuItem>
                      <MenuItem value="PayNow">UPI</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id="payment-note-input"
                      name="payment-note"
                      fullWidth
                      label="Payment Note"
                      value={paymentNote}
                      onChange={(e) => setPaymentNote(e.target.value)}
                      multiline
                      rows={2}
                      placeholder="Add any additional notes here"
                      inputProps={{
                        'aria-label': 'Payment note'
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClosePaymentDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitPayment}
            disabled={!paymentAmount || parseFloat(paymentAmount) <= 0 || parseFloat(paymentAmount) > creditSalesData.currentBalance || processingPayment}
            color="primary"
            startIcon={processingPayment ? <CircularProgress size={20} /> : null}
          >
            {processingPayment ? 'Processing...' : 'Process Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View All Transactions Dialog */}
      <Dialog
        open={allTransactionsDialogOpen}
        onClose={handleCloseAllTransactionsDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, height: '80vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" component="div">
            {showingHistoryLedger ? `Ledger - ${selectedHistoryCustomer && selectedHistoryCustomer.name}` : 'All Customers with Credit History'}
          </Typography>
          <IconButton
            onClick={handleCloseAllTransactionsDialog}
            size="small"
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {!showingHistoryLedger ? (
            // Customer Selection View
            <Box sx={{ p: 3 }}>
              {loadingAllCustomers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Loading customers with credit history...</Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom>
                    Select a customer to view their complete credit transaction history:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {historySearchTerm ?
                      `Showing ${filteredHistoryCustomers.length} of ${allCustomersWithHistory.length} customers` :
                      `Found ${allCustomersWithHistory.length} customers who have had credit transactions`
                    }
                  </Typography>

                  {/* Search Input */}
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Search Customers"
                    value={historySearchTerm}
                    onChange={handleHistorySearchChange}
                    InputProps={{
                      startAdornment: (
                        <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                    placeholder="Search by customer name..."
                    size="small"
                    sx={{ mb: 3 }}
                  />

                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>S.No</TableCell>
                          <TableCell>Customer Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Current Balance Due</TableCell>
                          <TableCell>Total Credit Sales</TableCell>
                          <TableCell>Total Payments</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredHistoryCustomers.map((customer, index) => (
                          <TableRow key={customer.id} hover>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{customer.name}</TableCell>
                            <TableCell>{customer.email}</TableCell>
                            <TableCell>{customer.phone}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(customer.balanceDue)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(customer.totalCreditSales)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(customer.totalPayments)}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleSelectHistoryCustomer(customer)}
                                startIcon={<LedgerIcon />}
                              >
                                View Ledger
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          ) : (
            // Customer Ledger View
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button
                  variant="outlined"
                  onClick={handleBackToCustomerList}

                  sx={{ mr: 2 }}
                >
                  Back to Customer List
                </Button>
                <Typography variant="h6">
                  Credit Transaction History for {selectedHistoryCustomer && selectedHistoryCustomer.name}
                </Typography>
              </Box>

              {/* Customer Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="primary">Opening Balance</Typography>
                      <Typography variant="h5">{formatCurrency((selectedHistoryCustomer && selectedHistoryCustomer.openingBalance) || 0)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="info.main">Total Credit Sales</Typography>
                      <Typography variant="h5">{formatCurrency((selectedHistoryCustomer && selectedHistoryCustomer.totalCreditSales) || 0)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="success.main">Total Payments</Typography>
                      <Typography variant="h5">{formatCurrency((selectedHistoryCustomer && selectedHistoryCustomer.totalPayments) || 0)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color={(selectedHistoryCustomer && selectedHistoryCustomer.balanceDue > 0) ? "error.main" : "success.main"}>
                        Current Balance Due
                      </Typography>
                      <Typography variant="h5">{formatCurrency((selectedHistoryCustomer && selectedHistoryCustomer.balanceDue) || 0)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Customer Details */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography><strong>Customer ID:</strong> {selectedHistoryCustomer && selectedHistoryCustomer.id}</Typography>
                    <Typography><strong>Email:</strong> {selectedHistoryCustomer && selectedHistoryCustomer.email}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography><strong>Phone:</strong> {selectedHistoryCustomer && selectedHistoryCustomer.phone}</Typography>
                    <Typography><strong>Contact Person:</strong> {selectedHistoryCustomer && selectedHistoryCustomer.contactPerson}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Transaction Table */}
              <Paper variant="outlined">
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6">Transaction History</Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Complete transaction history will be loaded here via Customer Ledger page integration
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate(`/customer-credit-ledger/${selectedHistoryCustomer && selectedHistoryCustomer.id}`)}
                    startIcon={<LedgerIcon />}
                    sx={{ mt: 2 }}
                  >
                    Open Full Ledger View
                  </Button>
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          {showingHistoryLedger && (
            <Button onClick={handleBackToCustomerList} variant="outlined">
              Back to Customer List
            </Button>
          )}
          <Button onClick={handleCloseAllTransactionsDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default CreditCustomersReport; 